import React, { useContext, useState } from "react";
import DocumentTitle from "react-document-title";
import * as Element from "@/screen/Map/ContractMapConfigurationStyles";
import Auth from "@/Auth/Auth";
import savingsPlans, { MapConfigurationData, MapConfigurationRepaymentObject } from "@/services/SavingsPlanService";
import { PlanOnboardingState, PlanOnboardingStep } from "@/services/SavingsPlanOnboarding.types";
import { ThemeContext } from "@fluentui/react-theme-provider";
import { RouteComponentProps, useHistory, useLocation } from "react-router";
import CustomLoader from "@/screen/Shared/CustomLoader";
import { ContractMapSectionHeader } from "@/screen/Map/Components/ContractMapSectionHeader";
import { ContractOnboardingStrings, getContractOnboardingStepCopy, resolveContractOnboardingError } from "@/screen/Map/contractOnboarding.strings";
import { resolveContractOnboardingStepNavigation } from "@/screen/Map/resolveContractOnboardingStepNavigation";
import { isStepActionable, resolveContractMapStepCardAction } from "@/screen/Map/resolveContractMapStepCardAction";
import { resolveContractMapPlanStatus } from "@/screen/Map/resolveContractMapPlanStatus";
import { resolveContractMapBlockedNotice } from "@/screen/Map/resolveContractMapBlockingStep";
import { ContractMapStepCard, ContractMapStepCardProps } from "@/screen/Map/Components/ContractMapStepCard";
import { ContractMapPlanStatusCard } from "@/screen/Map/Components/ContractMapPlanStatusCard";
import { ContractMapGuideBubble } from "@/screen/Map/Components/ContractMapGuideBubble";
import { getContractMapGuideTargetId, hasContractMapGuideTips } from "@/screen/Map/contractMapGuideTips";
import { MinorToAdultDataStepKey, buildMinorToAdultDataEditLocation, formatIdentityFullName } from "@/screen/Map/minorToAdultFlow";
import { advanceMinorToAdultGateSteps, useMinorToAdultHolder } from "@/screen/Map/useMinorToAdultHolder";

interface RouteParams { };
interface Props extends RouteComponentProps<RouteParams> { };

export const ContractMapConfiguration: React.FunctionComponent<Props> = () =>
{
    const userId = Auth.getUserProfile().id;
    const savingPlan = Auth.getActivePlan();
    const { state } = useLocation<any>();
    const history = useHistory();
    const savingPlanId = React.useRef<any>(state?.savingPlanId);
    const savingPlanName = React.useRef<any>(state?.savingPlanName);
    const [configurationMapData, setConfigurationMapData] = useState<MapConfigurationData>();
    const [onboardingState, setOnboardingState] = useState<PlanOnboardingState | null>(null);
    const [onboardingLoadFailed, setOnboardingLoadFailed] = useState<boolean>(false);
    const [link, setLink] = React.useState<string>("");
    const [isSaverPlan, setIsSaverPlan] = React.useState<boolean>();
    const [isAlchemyPlan, setIsAlchemyPlan] = React.useState<boolean>();
    const [isSponsoredPlan, setIsSponsoredPlan] = React.useState<boolean>();
    const [isExperiencePlan, setIsExperiencePlan] = React.useState<boolean>();
    const theme = useContext(ThemeContext);
    const [outdatedPlan, setOutdatedPlan] = React.useState<boolean>(false);
    const [repaymentObject, setRepaymentObject] = React.useState<MapConfigurationRepaymentObject>({ userSubscriptionId: 0, subscriptionId: 0, limitAmount: 0.0, type: "" });
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [isSubscriptionExpanded, setIsSubscriptionExpanded] = React.useState<boolean>(true);
    const [isSection2Expanded, setIsSection2Expanded] = React.useState<boolean>(true);
    const [isVerificationExpanded, setIsVerificationExpanded] = React.useState<boolean>(true);
    const [isPortfolioExpanded, setIsPortfolioExpanded] = React.useState<boolean>(true);
    const firstTimeLoading = React.useRef<boolean>(true);
    const [activeGuideStepKey, setActiveGuideStepKey] = React.useState<string | null>(null);
    const [isAdvancingReview, setIsAdvancingReview] = React.useState<boolean>(false);
    const [reviewAdvanceError, setReviewAdvanceError] = React.useState<string | null>(null);
    const minorToAdult = useMinorToAdultHolder(Number(savingPlanId.current), onboardingState?.currentStep);

    const areAllSectionsExpanded = isSubscriptionExpanded && isSection2Expanded && isVerificationExpanded && isPortfolioExpanded;

    const setAllSectionsExpanded = (expanded: boolean) =>
    {
        setIsSubscriptionExpanded(expanded);
        setIsSection2Expanded(expanded);
        setIsVerificationExpanded(expanded);
        setIsPortfolioExpanded(expanded);
    };

    const onExpandAllToggleChange = (_ev: React.MouseEvent<HTMLElement>, checked?: boolean) =>
    {
        setAllSectionsExpanded(Boolean(checked));
    };

    function chooseLink(step: string)
    {
        // Pasos de intervinientes: destino del mapa nuevo (onboardingState)
        if(step && step.startsWith("2"))
        {
            const navigation = resolveContractOnboardingStepNavigation(step, Number(savingPlanId.current));
            if(navigation.type === "push")
            {
                return navigation.pathname;
            }
            return "";
        }

        switch(step)
        {
            case "1.1":
            case "1.2":
            default:
                return "/savingsPlans/new";
            case "4.1":
                if(isExperiencePlan === true)
                {
                    return "/search";
                }
                if(isSaverPlan === true)
                {
                    return '/savingsPlans/iAmASaver/' + savingPlanId.current;
                }
                if(isAlchemyPlan === true)
                {
                    return '/savingsPlans/iLikeToDecide/' + savingPlanId.current;
                }
                if(isSponsoredPlan === true)
                {
                    return '/savingsPlans/iHaveExperience/' + savingPlanId.current;
                }

                return "/portfolio";
            case "4.2":
                return "/cart";
            case "4.3":
                return "/transfer";
            case "4.4":
                return "/distributor-switch";
        }
    }

    const navigateToOnboardingStep = (step: PlanOnboardingStep) =>
    {
        if(!isStepActionable(step))
        {
            return;
        }

        if(step.key === MinorToAdultDataStepKey && minorToAdult.adultOwner !== null)
        {
            history.push(buildMinorToAdultDataEditLocation(minorToAdult.adultOwner.id, Number(savingPlanId.current), savingPlanName.current));
            return;
        }

        const navigation = resolveContractOnboardingStepNavigation(step.key, Number(savingPlanId.current));

        if(navigation.type !== "push")
        {
            return;
        }

        // 1.1 en plan caducado: reenvía contexto de repayment al flujo de suscripción.
        if(step.key === "1.1" && outdatedPlan)
        {
            history.push(
            {
                pathname: navigation.pathname,
                state:
                {
                    subscriptionId: repaymentObject.subscriptionId,
                    userSubscriptionId: repaymentObject.userSubscriptionId,
                    subscriptionType: repaymentObject.type,
                    limitAmount: repaymentObject.limitAmount,
                },
            });
            return;
        }

        history.push(navigation.pathname, navigation.state);
    };

    // 3.2 / 3.3: advanceStep vuelve al flujo de corrección; al OK recargamos el mapa.
    const handleReviewNotValidatedStep = async (step: PlanOnboardingStep) =>
    {
        if(!isStepActionable(step) || isAdvancingReview)
        {
            return;
        }

        setReviewAdvanceError(null);
        setIsAdvancingReview(true);
        try
        {
            // Guarda de sincronía: avanzamos exactamente el paso de corrección que muestra la tarjeta (3.2 / 3.3).
            const result = await savingsPlans.advancePlanStep(Number(savingPlanId.current), step.key);
            if(result.success)
            {
                window.location.reload();
                return;
            }

            setReviewAdvanceError(resolveContractOnboardingError(result.errors?.[0], ContractOnboardingStrings.reviewAdvanceFailed));
        }
        finally
        {
            setIsAdvancingReview(false);
        }
    };

    React.useEffect(() =>
    {
        if(firstTimeLoading.current === true)
        {
            firstTimeLoading.current = false;

            // No caemos a Auth.getActivePlan(): el plan activo puede NO ser el que se está
            // contratando, y mostraríamos el mapa de otro plan. Si no sabemos qué plan cargar,
            // devolvemos al usuario a la lista de planes para que elija de nuevo.
            if(savingPlanId.current === undefined)
            {
                history.replace("/savingsPlans");
                return;
            }

            Promise.all(
            [
                savingsPlans.getMapConfigurationData(userId, savingPlanId.current),
                savingsPlans.getPlanOnboardingState(Number(savingPlanId.current)),
            ]).then(async ([mapData, onboardingEnvelope]) =>
            {
                if(!onboardingEnvelope.success || !onboardingEnvelope.data)
                {
                    setConfigurationMapData(mapData);
                    console.error("Error al cargar onboardingState", onboardingEnvelope.errors);
                    setOnboardingLoadFailed(true);
                    setOnboardingState(null);
                    setIsLoading(false);
                    return;
                }

                const loadedState = await advanceMinorToAdultGateSteps(Number(savingPlanId.current), onboardingEnvelope.data);
                setConfigurationMapData(mapData);
                setOnboardingLoadFailed(false);
                setOnboardingState(loadedState);

                // Expandir sección según currentStep del backend.
                if(loadedState.currentStep?.startsWith("3"))
                {
                    setIsVerificationExpanded(true);
                }
                if(loadedState.currentStep?.startsWith("4"))
                {
                    setIsPortfolioExpanded(true);
                }
                if(loadedState.currentStep === "4.2")
                {
                    setOutdatedPlan(true);
                    setLink("Repayment");
                }
            })
            .catch((error) =>
            {
                console.error("Error al cargar el mapa de configuración", error);
                setOnboardingLoadFailed(true);
                setIsLoading(false);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, savingPlan]);

    React.useEffect(() =>
    {
        if(configurationMapData !== undefined)
        {
            setIsSponsoredPlan(configurationMapData.isSponsoredPlan);
            setIsExperiencePlan(configurationMapData.isExperiencePlan);
            setIsSaverPlan(configurationMapData.isSaverPlan);
            setIsAlchemyPlan(configurationMapData.isAlchemyPlan);
            setRepaymentObject(configurationMapData.repaymentObject);

            if(configurationMapData.status === 5)
            {
                setOutdatedPlan(true);
                setLink("Repayment");
            }

            setIsLoading(false);
        }
    }, [configurationMapData]);

    React.useEffect(() =>
    {
        if(configurationMapData !== undefined && isAlchemyPlan !== undefined)
        {
            setLink(chooseLink(configurationMapData.mapStep));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAlchemyPlan, configurationMapData]);

    // Cabeceras activas: solo onboardingState.currentStep (fuente de verdad del mapa nuevo).
    const currentStepKey = onboardingState?.currentStep ?? "";
    const isSection1Active = currentStepKey.startsWith("1");
    const isSection2Active = currentStepKey.startsWith("2");
    // Sección 3: una sola tarjeta de paso (3.1 / 3.2 / 3.3) + tarjeta de estado del plan.
    const isSection3Active = currentStepKey.startsWith("3");
    const isSection4Active = currentStepKey.startsWith("4");
    const portfolioStep = isSection4Active ? currentStepKey : null;
    const isOutdatedStep = portfolioStep === "4.2";
    const isCancelledStep = portfolioStep === "4.3";
    // 4.1 = plan activo: botones operativos. En cualquier otro paso se muestra la misma
    // tarjeta en vista previa (botones desactivados) para que vean qué podrán hacer.
    const isPortfolioActive = portfolioStep === "4.1";
    const showPortfolioOperations = !isOutdatedStep && !isCancelledStep;
    const planStatus = resolveContractMapPlanStatus(onboardingState?.currentStep);

    const resolveStepNotice = (step: PlanOnboardingStep): string | undefined =>
    {
        return resolveContractMapBlockedNotice(step, onboardingState?.steps || []) ?? undefined;
    };

    const resolveMinorToAdultDataNotice = (fullName: string): string | undefined =>
    {
        switch(minorToAdult.status)
        {
            case "transforming":
                return ContractOnboardingStrings.minorToAdultTransforming(fullName);
            case "transformed":
                return ContractOnboardingStrings.minorToAdultTransformed(fullName);
            case "dataIncomplete":
                return ContractOnboardingStrings.minorToAdultDataIncomplete(fullName);
            case "failed":
                return ContractOnboardingStrings.minorToAdultFailed;
            default:
                return undefined;
        }
    };

    // Tras MinorToAdultHolder el plan original queda borrado y el nuevo es del ex-menor: aquí ya no hay nada que hacer.
    const resolveMinorToAdultDataCardOverrides = (step: PlanOnboardingStep, isActionDisabled: boolean): Partial<ContractMapStepCardProps> =>
    {
        if(step.key !== MinorToAdultDataStepKey || !step.isCurrent)
        {
            return {};
        }

        const fullName = minorToAdult.adultOwner ? formatIdentityFullName(minorToAdult.adultOwner) : "";
        const isHolderBusy = minorToAdult.status === "loading" || minorToAdult.status === "transforming";
        const overrides: Partial<ContractMapStepCardProps> =
        {
            isActionDisabled: isActionDisabled || isHolderBusy,
        };

        if(fullName !== "")
        {
            overrides.description = ContractOnboardingStrings.minorToAdultDataDescription(fullName);
        }

        const notice = resolveMinorToAdultDataNotice(fullName);
        if(notice !== undefined)
        {
            overrides.noticeText = notice;
        }

        if(minorToAdult.status === "transformed")
        {
            overrides.actionLabel = ContractOnboardingStrings.goToSavingsPlans;
            overrides.onActionClick = () => history.push("/savingsPlans");
            overrides.isActionDisabled = false;
        }

        return overrides;
    };

    const renderSubscriptionContent = () =>
    {
        const section1Steps = (onboardingState?.steps || []).filter((step) => step.key === "1.1" || step.key === "1.2");

        if(!isSubscriptionExpanded || section1Steps.length === 0)
        {
            return null;
        }

        return(
            <Element.AccordionSectionBody>
                <Element.AccordionSectionPanel theme={theme}>
                    <Element.AccordionSectionContent>
                        {section1Steps.map((step) =>
                        {
                            const copy = getContractOnboardingStepCopy(step.key);
                            const action = resolveContractMapStepCardAction(step, Number(savingPlanId.current));
                            const showAction = action.actionKind !== "none";

                            return(
                                <ContractMapStepCard
                                    key={step.key}
                                    title={copy.title}
                                    stepKey={step.key}
                                    description={copy.description || ""}
                                    extraDescription={copy.extraDescription}
                                    actionLabel={showAction ? action.actionLabel : undefined}
                                    onActionClick={showAction ? () => navigateToOnboardingStep(step) : undefined}
                                    isActionDisabled={action.isActionDisabled}
                                    isCurrent={action.isCurrent}
                                    isDimmed={action.isDimmed}
                                    noticeText={resolveStepNotice(step)}
                                />
                            );
                        })}
                    </Element.AccordionSectionContent>
                </Element.AccordionSectionPanel>
            </Element.AccordionSectionBody>
        );
    };

    // Sección 3: una sola tarjeta de paso (3.1 / 3.2 / 3.3) + tarjeta de estado del plan.
    const renderVerificationContent = () =>
    {
        // El backend emite un único paso 3.x aplicable (310 / 320 / 330).
        const section3Step = (onboardingState?.steps || []).find((step) => step.key.startsWith("3"));
        if(!section3Step)
        {
            return null;
        }

        const copy = getContractOnboardingStepCopy(section3Step.key);
        const action = resolveContractMapStepCardAction(section3Step, Number(savingPlanId.current));
        const showAction = action.actionKind !== "none";

        // 3.2 / 3.3: la descripción de la tarjeta es el texto de la incidencia registrada por el
        // admin (llega en onboardingState.incidenceDescription). Si no hay texto, cae al copy estático.
        const isCorrectionStep = section3Step.key === "3.2" || section3Step.key === "3.3";
        const incidenceText = onboardingState?.incidenceDescription?.trim();
        const cardDescription = isCorrectionStep && incidenceText
            ? incidenceText
            : (copy.description || "");

        return(
            <Element.AccordionSectionBody>
                <Element.AccordionSectionPanel theme={theme}>
                    <Element.AccordionSectionContent>
                        <ContractMapStepCard
                            title={copy.title}
                            stepKey={section3Step.key}
                            description={cardDescription}
                            isCurrent={action.isCurrent}
                            isDimmed={action.isDimmed}
                            noticeText={resolveStepNotice(section3Step)}
                            actionLabel={showAction ? action.actionLabel : undefined}
                            onActionClick={showAction ? () => handleReviewNotValidatedStep(section3Step) : undefined}
                            isActionDisabled={action.isActionDisabled || isAdvancingReview}
                        />
                        {reviewAdvanceError &&
                        (
                            <p style={{color: "#a4262c", marginTop: "0.75rem", cursor: "default"}}>
                                {reviewAdvanceError}
                            </p>
                        )}
                        <ContractMapPlanStatusCard
                            title={ContractOnboardingStrings.portfolioPlanTitle}
                            statusLabel={planStatus.label}
                            statusColor={planStatus.color}
                        />
                    </Element.AccordionSectionContent>
                </Element.AccordionSectionPanel>
            </Element.AccordionSectionBody>
        );
    };

    const navigateToPortfolioPath = (path: string) =>
    {
        if(!path)
        {
            return;
        }

        history.push(path);
    };

    // Sección 4: 4.1 se muestra siempre (vista previa con botones off si el plan no está
    // activo). 4.2 / 4.3 solo cuando el backend marca esos pasos.
    const renderPortfolioContent = () =>
    {
        const copy41 = getContractOnboardingStepCopy("4.1");
        const copy42 = getContractOnboardingStepCopy("4.2");
        const copy43 = getContractOnboardingStepCopy("4.3");
        const portfolio41Description = isPortfolioActive
            ? (copy41.description || "")
            : ContractOnboardingStrings.portfolioOperationsPreviewDescription;

        return(
            <Element.AccordionSectionBody>
                <Element.AccordionSectionPanel theme={theme}>
                    <Element.AccordionSectionContent>
                        {showPortfolioOperations &&
                        (
                            <ContractMapStepCard
                                title={copy41.title}
                                stepKey="4.1"
                                description={portfolio41Description}
                                isCurrent={isPortfolioActive}
                                isDimmed={!isPortfolioActive}
                                tasks={
                                [
                                    {
                                        title: ContractOnboardingStrings.buyFundsTaskTitle,
                                        actionLabel: ContractOnboardingStrings.buy,
                                        onActionClick: () => navigateToPortfolioPath(link || chooseLink("4.1")),
                                        isActionDisabled: !isPortfolioActive,
                                    },
                                    {
                                        title: ContractOnboardingStrings.transferFundsTaskTitle,
                                        description: ContractOnboardingStrings.transferFundsTaskDescription,
                                        actionLabel: ContractOnboardingStrings.goToTransfers,
                                        onActionClick: () => navigateToPortfolioPath("/purchase-transfer"),
                                        isActionDisabled: !isPortfolioActive,
                                    },
                                    {
                                        title: ContractOnboardingStrings.distributorSwitchTaskTitle,
                                        description: ContractOnboardingStrings.distributorSwitchTaskDescription,
                                        actionLabel: ContractOnboardingStrings.go,
                                        onActionClick: () => navigateToPortfolioPath("/distributor-switch"),
                                        isActionDisabled: !isPortfolioActive,
                                    }
                                ]}
                            />
                        )}
                        {isOutdatedStep &&
                        (
                            <ContractMapStepCard
                                title={copy42.title}
                                stepKey="4.2"
                                description={copy42.description || ""}
                                isCurrent={true}
                                actionLabel={ContractOnboardingStrings.resubscribe}
                                onActionClick={() =>
                                {
                                    history.push(
                                    {
                                        pathname: "/Repayment",
                                        state:
                                        {
                                            subscriptionId: repaymentObject.subscriptionId,
                                            userSubscriptionId: repaymentObject.userSubscriptionId,
                                            subscriptionType: repaymentObject.type,
                                            limitAmount: repaymentObject.limitAmount,
                                        },
                                    });
                                }}
                            />
                        )}
                        {isCancelledStep &&
                        (
                            <ContractMapStepCard
                                title={copy43.title}
                                stepKey="4.3"
                                description=
                                {
                                    <React.Fragment>
                                        {copy43.description}
                                        <strong>{ContractOnboardingStrings.supportEmail}</strong>.
                                    </React.Fragment>
                                }
                                isCurrent={isCancelledStep}
                            />
                        )}
                    </Element.AccordionSectionContent>
                </Element.AccordionSectionPanel>
            </Element.AccordionSectionBody>
        );
    };

    // Nombre a mostrar en el título. Solo lo resolvemos desde el state de navegación:
    // NO caemos a Auth.getActivePlan().name porque el plan activo puede ser otro distinto
    // del que se está mostrando y pintaríamos un nombre equivocado. Si no lo conocemos,
    // el título queda como "Mapa de Configuración" a secas.
    const resolvedPlanName: string | undefined = savingPlanName.current;
    const mapConfigurationTitle = resolvedPlanName ? `Mapa de Configuración - ${resolvedPlanName}` : "Mapa de Configuración";

    return(
        <React.Fragment>
            <DocumentTitle title="IronIA - Mapa de Configuración">
                <React.Fragment>
                    {isLoading === true &&
                    (
                        <React.Fragment>
                            <CustomLoader text="TU PERSONAL STORE FINANCIERO" />
                            <Element.LoadingContainer>
                                <Element.LoadingTitle>{resolvedPlanName ? `Cargando plan de cartera: ${resolvedPlanName}` : "Cargando plan de cartera"}</Element.LoadingTitle>
                            </Element.LoadingContainer>
                        </React.Fragment>
                    )}
                    {isLoading === false && onboardingLoadFailed &&
                    (
                        <React.Fragment>
                            <Element.PageTitle>
                                {mapConfigurationTitle}
                            </Element.PageTitle>
                            <Element.MapSeparator />
                            <Element.LoadingContainer>
                                <Element.LoadingTitle>
                                    {ContractOnboardingStrings.mapLoadError}
                                </Element.LoadingTitle>
                            </Element.LoadingContainer>
                        </React.Fragment>
                    )}
                    {isLoading === false && !onboardingLoadFailed &&
                    (
                        <React.Fragment>
                            <Element.PageTitleHeader>
                                <Element.PageTitle>
                                    {mapConfigurationTitle}
                                </Element.PageTitle>
                                <Element.PageTitleExpandToggle
                                    theme={theme}
                                    checked={areAllSectionsExpanded}
                                    onText={ContractOnboardingStrings.collapseAllSections}
                                    offText={ContractOnboardingStrings.expandAllSections}
                                    onChange={onExpandAllToggleChange}
                                />
                            </Element.PageTitleHeader>
                            <Element.MapSeparator />
                            <Element.AccordionColumn className="ms-Grid-col ms-sm12 ms-md12 ms-lg12 ms-xl3 ms-xxl3 ms-xxxl3">
                                <ContractMapSectionHeader
                                    text={ContractOnboardingStrings.section1Title}
                                    isCurrent={isSection1Active}
                                    isExpanded={isSubscriptionExpanded}
                                    setIsExpanded={setIsSubscriptionExpanded}
                                />
                                {renderSubscriptionContent()}
                            </Element.AccordionColumn>
                            <Element.AccordionColumn className="ms-Grid-col ms-sm12 ms-md12 ms-lg12 ms-xl3 ms-xxl3 ms-xxxl3">
                                <ContractMapSectionHeader
                                    text={ContractOnboardingStrings.section2Title}
                                    isCurrent={isSection2Active}
                                    isExpanded={isSection2Expanded}
                                    setIsExpanded={setIsSection2Expanded}
                                />
                                {isSection2Expanded && (() =>
                                {
                                    // El backend ya filtra aplicables y ordena: pintamos todas las de sección 2
                                    // con el mismo componente (2.1, 2.2, 2.2.x, 2.3...).
                                    const section2Steps = (onboardingState?.steps || []).filter((s) => s.key.startsWith("2"));

                                    if(section2Steps.length === 0)
                                    {
                                        return null;
                                    }

                                    return(
                                        <Element.AccordionSectionBody>
                                            <Element.AccordionSectionPanel theme={theme}>
                                                <Element.AccordionSectionContent>
                                                    {section2Steps.map((step) =>
                                                    {
                                                        const copy = getContractOnboardingStepCopy(step.key);
                                                        const action = resolveContractMapStepCardAction(step, Number(savingPlanId.current));
                                                        const shouldShowGuide = hasContractMapGuideTips(step.key);

                                                        return(
                                                            <ContractMapStepCard
                                                                key={step.key}
                                                                title={copy.title}
                                                                stepKey={step.key}
                                                                description={copy.description || ""}
                                                                extraDescription={copy.extraDescription}
                                                                actionLabel={action.actionLabel}
                                                                onActionClick={() => navigateToOnboardingStep(step)}
                                                                isActionDisabled={action.isActionDisabled}
                                                                isCurrent={action.isCurrent}
                                                                isDimmed={action.isDimmed}
                                                                noticeText={resolveStepNotice(step)}
                                                                onGuideClick={shouldShowGuide ? () => setActiveGuideStepKey(step.key) : undefined}
                                                                guideButtonId={shouldShowGuide ? getContractMapGuideTargetId(step.key) : undefined}
                                                                {...resolveMinorToAdultDataCardOverrides(step, action.isActionDisabled)}
                                                            />
                                                        );
                                                    })}
                                                </Element.AccordionSectionContent>
                                            </Element.AccordionSectionPanel>
                                        </Element.AccordionSectionBody>
                                    );
                                })()}
                            </Element.AccordionColumn>
                            <Element.AccordionColumn className="ms-Grid-col ms-sm12 ms-md12 ms-lg12 ms-xl3 ms-xxl3 ms-xxxl3">
                                <ContractMapSectionHeader
                                    text={ContractOnboardingStrings.section3Title}
                                    isCurrent={isSection3Active}
                                    isExpanded={isVerificationExpanded}
                                    setIsExpanded={setIsVerificationExpanded}
                                />
                                {isVerificationExpanded === true && renderVerificationContent()}
                            </Element.AccordionColumn>
                            <Element.AccordionColumn className="ms-Grid-col ms-sm12 ms-md12 ms-lg12 ms-xl3 ms-xxl3 ms-xxxl3">
                                <ContractMapSectionHeader
                                    text={ContractOnboardingStrings.section4Title}
                                    isCurrent={isSection4Active}
                                    isExpanded={isPortfolioExpanded}
                                    setIsExpanded={setIsPortfolioExpanded}
                                />
                                {isPortfolioExpanded === true && renderPortfolioContent()}
                            </Element.AccordionColumn>
                            <ContractMapGuideBubble
                                isOpen={activeGuideStepKey !== null}
                                stepKey={activeGuideStepKey || ""}
                                onDismiss={() => setActiveGuideStepKey(null)}
                                targetId={activeGuideStepKey ? getContractMapGuideTargetId(activeGuideStepKey) : undefined}
                            />
                        </React.Fragment>
                    )}
                </React.Fragment>
            </DocumentTitle>
        </React.Fragment>
    );
}